import React from 'react';
import { PatientStatus, STATUS_CONFIG, Patient } from '../types';
import { Check, Clock, Activity } from 'lucide-react';

interface TimelineProps {
  patient: Patient;
}

const Timeline: React.FC<TimelineProps> = ({ patient }) => {
  const patientConfig = STATUS_CONFIG[patient.currentStatus] || { step: 6 };
  const currentStep = patientConfig.step;
  const steps = [
    PatientStatus.WAITING,
    PatientStatus.PREPARATION,
    PatientStatus.ROOM_ENTRY,
    PatientStatus.ANESTHESIA,
    PatientStatus.SURGERY,
    PatientStatus.RECOVERY,
    PatientStatus.EXIT
  ];

  return (
    <div className="w-full max-w-2xl mx-auto mt-8">
      <div className="relative">
        {/* Progress Bar Background */}
        <div className="absolute left-6 top-0 bottom-0 w-1 bg-slate-200 md:left-0 md:top-6 md:w-full md:h-1" />

        <div className="flex flex-col md:flex-row justify-between relative">
          {steps.map((status, index) => {
            const config = STATUS_CONFIG[status];
            const isCompleted = currentStep > config.step;
            const isCurrent = currentStep === config.step;

            // Find timestamp if exists
            const log = patient.logs.find(l => l.status === status);
            const timeString = log
              ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : '';

            return (
              <div key={status} className="flex md:flex-col items-center mb-8 md:mb-0 relative z-10 group">
                {/* Icon Circle */}
                <div
                  className={`
                    w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500
                    ${isCompleted ? 'bg-green-500 border-green-500' : ''}
                    ${isCurrent ? `${config.color} border-white shadow-lg scale-110` : ''}
                    ${!isCompleted && !isCurrent ? 'bg-white border-slate-200' : ''}
                  `}
                >
                  {isCompleted ? (
                    <Check className="w-6 h-6 text-white" />
                  ) : isCurrent ? (
                    status === PatientStatus.SURGERY ? <Activity className="w-6 h-6 text-white animate-pulse" /> :
                      <Clock className={`w-6 h-6 ${config.text}`} />
                  ) : (
                    <div className="w-3 h-3 bg-slate-200 rounded-full" />
                  )}
                </div>

                {/* Text Content */}
                <div className={`ml-4 md:ml-0 md:mt-4 md:text-center w-full md:w-32 ${isCurrent ? 'opacity-100' : 'opacity-60'}`}>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Paso {index + 1}</p>
                  <p className={`text-sm font-bold ${isCurrent ? 'text-slate-900 scale-105' : 'text-slate-600'} transition-transform`}>
                    {config.label}
                  </p>

                  {timeString && (
                    <p className="text-xs text-slate-500 mt-1 font-mono bg-slate-100 inline-block px-2 py-0.5 rounded">
                      {timeString}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline;